import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from '../../../../config/TranslationProvider';
import {
    addHours,
    endOfToday,
    endOfTomorrow,
    formatISO,
    getHours,
    getMinutes,
    getSeconds,
    setDay,
    setHours,
    startOfToday,
    startOfTomorrow,
} from 'date-fns';
import DateRangePickerOverlay from './DateRangePickerOverlay';
import DateRangeInput from './DateRangeInput';

import './DateRangePicker.scss';
import {useAppConfiguration} from '../../../../AppConfiguration';

const isStartOrEndOfDay = date => (
    (getHours(date) === 0 && getMinutes(date) === 0 && getSeconds(date) === 0)
    || (getHours(date) === 23 && getMinutes(date) === 59 && getSeconds(date) === 59)
);

// Will be used to find out the selected type based on the passed start and end date and to set those values when
// selecting a different type.
// "date_range" is the fallback type when no other type matches and has therefore no values.
export const dateRangeTypes = Object.fromEntries(Object.entries({
    // 'type': [startDate, endDate]
    'arbitrary': [startOfToday(), ''], // Only show future events, '' means that no endDate is set
    'today': [startOfToday(), endOfToday()],
    'tomorrow': [startOfTomorrow(), endOfTomorrow()],
    // We start the weekend on friday 15:00
    'this_weekend': [setHours(setDay(startOfToday(), 5), 15), setDay(endOfToday(), 7)],
    'next_week': [setDay(startOfToday(), 8), setDay(endOfToday(), 14)],
    'date_range': null, // This is the fallback, it has no values
}).map(([key, startAndEndOrNull]) => {
    if (startAndEndOrNull === null) {
        return [key, startAndEndOrNull];
    }

    let [from, to] = startAndEndOrNull;

    // If from or to is set to the start or end of the day, set the time to 6am instead, as we sometimes have events
    // that go on until deep into the night, and we don't want to show an event that already ended at 5am today as a
    // "today event"
    if (from && isStartOrEndOfDay(from)) {
        from = addHours(from, 6);
    }
    if (to && isStartOrEndOfDay(to)) {
        to = addHours(to, 6);
    }

    return [key, [from, to]];
}));

// Format all dates as ISO
for (const key in dateRangeTypes) {
    if (dateRangeTypes[key] !== null) {
        dateRangeTypes[key] = dateRangeTypes[key].map(value => value === '' ? value : formatISO(value));
    }
}

const DateRangePicker = ({register, selectedStartDate, selectedEndDate, inline}) => {
    const {filterConfiguration} = useAppConfiguration();
    const dateDefault = filterConfiguration.dateOption?.default || 'arbitrary';

    const isValidDefaultDate = dateDefault in dateRangeTypes;
    const translations = useTranslation('date_range_picker');
    const [startDate, setStartDate] = useState(
        selectedStartDate ||
        (isValidDefaultDate && dateRangeTypes[dateDefault][0]) ||
        dateRangeTypes['arbitrary'][0]
    );
    const [endDate, setEndDate] = useState(
        selectedEndDate ||
        (isValidDefaultDate && dateRangeTypes[dateDefault][1])
        || dateRangeTypes['arbitrary'][1]
    );

    // Determine the selected date range type
    const selectedType = useMemo(() => {

        // Use the default date defined by the configuration if no date is selected
        if (!selectedStartDate && !selectedEndDate && dateDefault && isValidDefaultDate) {
            return dateDefault;
        }

        // Find a matching type
        for (const type in dateRangeTypes) {
            const values = dateRangeTypes[type];

            if (values === null) {
                // Skip types without any values to compare against
                continue;
            }

            if (startDate === values[0] && endDate === values[1]) {
                return type;
            }
        }

        // No fitting type found, use the date_range type
        return 'date_range';
    }, [selectedStartDate, selectedEndDate, dateDefault, isValidDefaultDate, startDate, endDate]);

    // Show date range picker on mount?
    const [showDateRangePicker, setShowDateRangePicker] = useState(selectedType === 'date_range');
    const [showDateRangePickerOverlay, setShowDateRangePickerOverlay] = useState(false);

    function setDatesByType(selectedType) {
        const dateRangeSelected = selectedType === 'date_range';
        setShowDateRangePicker(dateRangeSelected);
        setShowDateRangePickerOverlay(dateRangeSelected);

        const values = dateRangeTypes[selectedType];
        if (values !== null) {
            setStartDate(values[0]);
            setEndDate(values[1]);
        }
    }

    return (
        <div className={inline ? 'single_search_field date-wrapper' : 'form-group'}>
            {/* We want events ending after the start date and starting before the end date */}
            <input ref={register} type="hidden" name="end_date_from" value={startDate} aria-label="date-range-start-date" />
            <input ref={register} type="hidden" name="start_date_to" value={endDate} aria-label="date-range-end-date" />

            {showDateRangePickerOverlay && (
                <DateRangePickerOverlay
                    startDate={startDate}
                    inline={inline}
                    endDate={endDate || startDate}
                    translations={translations}
                    onChange={(startDate, endDate) => {
                        setStartDate(startDate);
                        setEndDate(endDate);
                        setShowDateRangePickerOverlay(false);
                    }}
                    setShowOverlay={setShowDateRangePickerOverlay}
                />
            )}

            <div className={classnames({
                'date-range-picker': true,
                'date-range-input-visible': showDateRangePicker,
            })}>
                <div className="select-basic date-range-type">
                    <select
                        className="form-control"
                        value={showDateRangePicker ? 'date_range' : selectedType}
                        onChange={event => setDatesByType(event.target.value)}
                        aria-label="date-range"
                    >
                        {Object.keys(dateRangeTypes).map(type => (
                            <option key={type} value={type}>
                                {/* eslint-disable-next-line react-hooks/rules-of-hooks */}
                                {useTranslation(type)}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="date-range-input">
                    <DateRangeInput
                        startDate={startDate}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                        endDate={endDate || startDate}
                        onFocus={() => setShowDateRangePickerOverlay(true)}
                        inline={inline}
                    />
                </div>
            </div>
        </div>
    );
};

export default DateRangePicker;
